import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { alpha } from '@material-ui/core/styles/colorManipulator';
import clsx from 'clsx';

import { Spinner as SpinnerIcon } from '../../icons';

export interface LoadingSpinnerProps {
  bgOverlay?: boolean;
  className?: string;
  style?: 'primary' | 'secondary';
  variant?: 'inline' | 'panel' | 'page';
}

const useStyles = makeStyles((theme: Theme) => ({
  loadingSpinner: {
    alignItems: 'center',
    display: 'flex',
    height: '100vh',
    justifyContent: 'center',
  },

  loaderInline: {
    display: 'inline-flex',
    height: 'auto',
  },

  loaderPanel: {
    height: 'auto',
    minHeight: '120px',
  },

  backgroundOverlay: {
    backgroundColor: alpha(theme.palette.common.white, 0.5),
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: theme.zIndex.snackbar,
  },

  stylePrimary: {
    '& svg': {
      color: theme.palette.primary.main,
    },
  },

  styleSecondary: {
    '& svg': {
      color: theme.palette.grey[800],
    },
  },
}));

export const LoadingSpinner = (props: LoadingSpinnerProps): JSX.Element => {
  const classes = useStyles();
  const {
    bgOverlay = false,
    className = '',
    style = 'primary',
    variant = 'page',
  } = props;

  let spinnerSize;
  switch (variant) {
    case 'inline':
      spinnerSize = 's';
      break;
    case 'panel':
      spinnerSize = 'l';
      break;
    default:
      spinnerSize = 'xl';
      break;
  }

  const loadingSpinnerStyle = clsx(
    classes.loadingSpinner,
    style === 'primary' && classes.stylePrimary,
    style === 'secondary' && classes.styleSecondary,
    variant === 'inline' && classes.loaderInline,
    variant === 'panel' && classes.loaderPanel,
    bgOverlay && classes.backgroundOverlay,
    className || '',
  );

  const loadingTestId = `loading-spinner--${variant}`;

  return (
    <div className={loadingSpinnerStyle} data-fd={loadingTestId}>
      <SpinnerIcon size={spinnerSize} />
    </div>
  );
};
